const VAT_DIVISOR = 1.2;

const applyPrices = (record, config) => {
  const fields = { ...record.fields };
  const ttcKey = config.itemPriceFieldTTC;
  const htKey = config.itemPriceFieldHT;
  const rawCatalogueTtc = fields[ttcKey];
  const rawCatalogueHt = htKey ? fields[htKey] : undefined;
  const overrideVal = config.pricesOverride?.[record.id];

  const cataloguePriceTTC =
    rawCatalogueTtc != null && rawCatalogueTtc !== ""
      ? Number(rawCatalogueTtc)
      : rawCatalogueTtc;

  let cataloguePriceHT = null;
  if (rawCatalogueHt != null && rawCatalogueHt !== "") {
    cataloguePriceHT = Number(rawCatalogueHt);
  } else if (
    typeof cataloguePriceTTC === "number" &&
    Number.isFinite(cataloguePriceTTC)
  ) {
    cataloguePriceHT = cataloguePriceTTC / VAT_DIVISOR;
  }

  if (overrideVal !== undefined) {
    fields[ttcKey] = overrideVal;
  }

  const hasOverride = overrideVal !== undefined;
  const priceOverrideTTC = hasOverride ? Number(overrideVal) : null;
  const priceOverrideHT = hasOverride ? Number(overrideVal) / VAT_DIVISOR : null;

  return {
    ...fields,
    cataloguePriceTTC,
    cataloguePriceHT,
    priceOverrideTTC,
    priceOverrideHT,
  };
};

/** Airtable fields that contribute navigation filters (not Tags — Tags are badges). */
const FILTER_SOURCE_FIELDS = [
  "Filtres",
  "Filters",
  "Filter",
  "Categories",
  "Catégories",
];

function normalizeFilterValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

function splitFilterString(value) {
  return String(value)
    .split(/[;,/|]/)
    .map((item) => normalizeFilterValue(item))
    .filter(Boolean);
}

function tokensFromFilterItem(item) {
  if (item == null) return [];
  if (typeof item === "string") return splitFilterString(item);
  if (typeof item === "object") {
    const label = item.name ?? item.Name ?? item.value;
    if (typeof label === "string") return splitFilterString(label);
  }
  return [];
}

/**
 * Optional synonyms: merge legacy Airtable spellings into one chip slug.
 * Any value not listed here uses its normalized Airtable text as the slug (e.g. pool, kids).
 */
const FILTER_SLUG_ALIASES = {
  underwater: "underwater",
  sublue: "underwater",
  seabob: "underwater",
  "e-foil": "efoil",
  "e-foil-scooter": "efoil",
  efoil: "efoil",
  "e-surf": "surf",
  jetsurf: "surf",
  surf: "surf",
  tapis: "inflatable",
  "piscine-gonflable": "inflatable",
  "plateforme-gonflable": "inflatable",
  kid: "kids",
  platform: "platforms",
  platforms: "platforms",
  inflatable: "inflatable",
  piscine: "pool",
};

function normalizedTokenToSlug(token) {
  if (!token) return null;
  return FILTER_SLUG_ALIASES[token] || token;
}

function collectRawFilterTokens(fields) {
  const tokens = [];
  for (const fieldName of FILTER_SOURCE_FIELDS) {
    const raw = fields?.[fieldName];
    if (!raw) continue;
    if (Array.isArray(raw)) {
      for (const item of raw) {
        tokens.push(...tokensFromFilterItem(item));
      }
    } else if (typeof raw === "string") {
      tokens.push(...splitFilterString(raw));
    }
  }
  return tokens;
}

/**
 * Slugs for grid chips: normalized Airtable values plus optional aliases (synonyms).
 */
function resolveToyFilterSlugs(fields) {
  const slugs = new Set();
  for (const token of collectRawFilterTokens(fields)) {
    const slug = normalizedTokenToSlug(token);
    if (slug) slugs.add(slug);
  }
  if (slugs.size === 0) {
    const cat = fields?.["Catégorie"];
    if (cat && typeof cat === "string") {
      const slug = normalizedTokenToSlug(normalizeFilterValue(cat));
      if (slug) slugs.add(slug);
    }
  }
  return Array.from(slugs);
}

function sortKeyPosition(fields) {
  const p = fields?.Position;
  const n = Number(p);
  return Number.isFinite(n) ? n : 999;
}

export async function getToys(config) {
  const response = await fetch(
    "https://n8n.prestigiousyachting.com/webhook/getToys"
  );
  const records = await response.json();
  const recordsById = records.data.reduce((acc, record) => {
    acc[record.id] = record;
    return acc;
  }, {});
  const formattedRecords = records.data.map((record) => {
    if (
      record.fields["Catégorie"] === "Package" &&
      record.fields["Produit (from Produit dans package)"]
    ) {
      return {
        id: record.id,
        fields: {
          ...applyPrices(record, config),
          toys: record.fields["Produit (from Produit dans package)"]
            .map((productId, index) => {
              const toyRecord = recordsById[productId];
              return toyRecord
                ? {
                    id: toyRecord.id,
                    fields: {
                      ...applyPrices(toyRecord, config),
                      quantityInPackage:
                        record.fields["Quantité dans package"]?.[index] || 1,
                    },
                  }
                : null;
            })
            .filter(Boolean),
        },
      };
    }
    const fields = record.fields || {};
    const disponible = Number(fields["Quantité disponible"] ?? 0);
    const extra = Number(fields["Quantité extra"] ?? 0);
    const tagsRaw = fields["Tags"];
    const displayBadge =
      typeof tagsRaw === "string" ? tagsRaw.trim() : String(tagsRaw || "").trim();

    return {
      id: record.id,
      fields: applyPrices(record, config),
      filters: resolveToyFilterSlugs(fields),
      maxStock: disponible + extra,
      ...(displayBadge ? { displayBadge } : {}),
    };
  });

  return formattedRecords
    .filter((record) => record.fields["Catégorie"] !== "Package")
    .sort(
      (a, b) => sortKeyPosition(a.fields) - sortKeyPosition(b.fields)
    );
}
